"use client";

import { useState } from "react";
import {
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { RESULT_CARD, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  CfuPanel,
  SeriesPanel,
} from "@/components/tools/biology-lab/serial-dilution-panels";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberList } from "@/lib/parse-number";
import { plain } from "@/lib/tool-kit/biology-lab/format";
import {
  type CountRangeId,
  MAX_STEPS,
} from "@/lib/tool-kit/biology-lab/serial-dilution";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const SERIAL_DILUTION_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to work out?",
  modeSeries: "Plan a dilution series",
  modeCfu: "Back-calculate CFU/mL from plate counts",
  startConc: "Starting concentration (optional)",
  concUnit: "Concentration unit (optional)",
  concUnitPlaceholder: "CFU/mL",
  concUnitDefault: "CFU/mL",
  factor: "Dilution factor per step",
  factorHint: "10 means 1 part sample into 9 parts diluent.",
  steps: "Number of tubes",
  stepsHint: "1 to {max}.",
  volume: "Volume in each tube after mixing",
  volumeUnit: "Volume unit",
  unitUl: "µL",
  unitMl: "mL",
  results: "Results",
  transferEach: "Transfer per step",
  diluentEach: "Diluent per tube",
  diluentTotal: "Total diluent needed",
  colTube: "Tube",
  colOverall: "Overall dilution",
  colConcentration: "Concentration",
  colTransfer: "Transfer in",
  colDiluent: "Diluent",
  colLeft: "Left after next transfer",
  fromStock: "from stock",
  fromPrevious: "from previous tube",
  none: "—",
  working: "The working",
  workingTransfer:
    "transfer = volume ÷ factor = {volume} ÷ {factor} = {transfer}",
  workingDiluent:
    "diluent = volume − transfer = {volume} − {transfer} = {diluent}",
  seriesNote:
    "Volumes are in {unit}. Mix each tube thoroughly before you take the next transfer, and use a fresh tip at every step.",
  copyTable: "Copy table",
  copyResult: "Copy result",
  copied: "Copied!",
  errFactor: "The dilution factor must be greater than 1.",
  errSteps: "The number of tubes must be a whole number from 1 to {max}.",
  errVolume: "The tube volume must be greater than 0.",
  errStart: "The starting concentration must be greater than 0.",
  countsLabel: "Colony counts (one per plate, separated by commas)",
  countsHint: "Enter replicate plates of the same dilution.",
  platedTube: "Tube you plated from",
  platedTubeOption: "Tube {n} (1 in {dilution})",
  platedVolume: "Volume plated",
  platedUnit: "Plated volume unit",
  preDilution: "Dilution made before tube 1",
  preDilutionHint:
    "1 if the sample went straight into the series; 10 for 1 g in 9 mL.",
  rangeLabel: "Countable range",
  rangeTextbook: "30–300 colonies (textbook, AOAC, USDA)",
  rangeApha: "25–250 colonies (APHA)",
  rangeIso: "15–300 colonies (ISO, FDA BAM from 2025)",
  cfuPerMl: "CFU/mL in the original sample",
  meanColonies: "Mean colonies per plate",
  platesCounted: "Plates counted",
  platesHeading: "Is each plate countable?",
  plateOk: "Plate {n}: {count} colonies — inside the {low}–{high} range.",
  plateLow:
    "Plate {n}: {count} colonies — below {low}. Too few for a reliable count; plate from a less dilute tube.",
  plateHigh:
    "Plate {n}: {count} colonies — above {high}. Too crowded to count; plate from a more dilute tube.",
  workingCfu:
    "CFU/mL = mean colonies × overall dilution factor ÷ volume plated (mL)",
  workingCfuNumbers: "CFU/mL = {mean} × {dilution} ÷ {plated} = {result}",
  cfuNote:
    "Only plates inside the countable range belong in the average. Colonies from a plate outside it are estimates.",
  copySummary: "{value} CFU/mL",
  errCounts: "Enter at least one colony count of 0 or more.",
  errCumulative: "The overall dilution must be 1 or more.",
  errPlated: "The plated volume must be greater than 0.",
  errPre: "The pre-dilution factor must be 1 or more.",
  enter: "Enter a dilution factor, a number of tubes and a tube volume.",
} as const;

export type SerialDilutionCalculatorStrings = {
  [K in keyof typeof SERIAL_DILUTION_CALCULATOR_STRINGS]: string;
};

type Mode = "series" | "cfu";
type VolumeUnit = "ul" | "ml";

const ID = "sd";
const ML_PER_UL = 0.001;

function stepsFrom(raw: string): number {
  const value = parseMeasuredNumber(raw);
  return value === null ? Number.NaN : value;
}

export default function SerialDilutionCalculatorWidget() {
  const rawStrings = useToolUi(SERIAL_DILUTION_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const strings = {
    ...rawStrings,
    errSteps: t(rawStrings.errSteps, { max: String(MAX_STEPS) }),
  };
  const [mode, setMode] = useState<Mode>("series");
  const [startConc, setStartConc] = useState("4e8");
  const [concUnit, setConcUnit] = useState<string>(strings.concUnitDefault);
  const [factor, setFactor] = useState("10");
  const [steps, setSteps] = useState("8");
  const [volume, setVolume] = useState("1000");
  const [volumeUnit, setVolumeUnit] = useState<VolumeUnit>("ul");
  const [counts, setCounts] = useState("42, 38");
  const [platedTube, setPlatedTube] = useState("6");
  const [platedVolume, setPlatedVolume] = useState("100");
  const [platedUnit, setPlatedUnit] = useState<VolumeUnit>("ul");
  const [preDilution, setPreDilution] = useState("1");
  const [range, setRange] = useState<CountRangeId>("textbook");

  const factorValue = parseMeasuredNumber(factor);
  const stepCount = stepsFrom(steps);
  const volumeValue = parseMeasuredNumber(volume);
  const unitLabels: Record<VolumeUnit, string> = {
    ul: strings.unitUl,
    ml: strings.unitMl,
  };
  const unitOptions = [
    { value: "ul" as const, label: strings.unitUl },
    { value: "ml" as const, label: strings.unitMl },
  ];
  const tubeCount =
    Number.isInteger(stepCount) && stepCount >= 1 && stepCount <= MAX_STEPS
      ? stepCount
      : 0;
  const tubeOptions = Array.from({ length: tubeCount }, (_, index) => ({
    value: String(index + 1),
    label: t(strings.platedTubeOption, {
      n: String(index + 1),
      dilution: plain(
        fmt,
        (factorValue !== null && factorValue > 1 ? factorValue : 10) **
          (index + 1)
      ),
    }),
  }));
  const plated = parseMeasuredNumber(platedVolume);
  const platedMl =
    plated === null ? null : plated * (platedUnit === "ul" ? ML_PER_UL : 1);
  const tubeIndex = Number(platedTube);
  const cumulative =
    factorValue !== null && factorValue > 1 ? factorValue ** tubeIndex : null;
  const pre = parseMeasuredNumber(preDilution);

  function renderResults() {
    if (mode === "series") {
      if (factorValue === null || volumeValue === null) {
        return <Notice>{strings.enter}</Notice>;
      }
      return (
        <div className={`${RESULT_CARD} space-y-4`}>
          <SeriesPanel
            concUnit={concUnit.trim()}
            fmt={fmt}
            input={{
              startConc: parseMeasuredNumber(startConc),
              factor: factorValue,
              steps: stepCount,
              volume: volumeValue,
            }}
            strings={strings}
            volumeUnit={unitLabels[volumeUnit]}
          />
        </div>
      );
    }
    if (platedMl === null || cumulative === null || pre === null) {
      return <Notice>{strings.enter}</Notice>;
    }
    return (
      <div className={`${RESULT_CARD} space-y-4`}>
        <CfuPanel
          fmt={fmt}
          input={{
            counts: parseNumberList(counts),
            cumulative,
            preDilution: pre,
            platedMl,
          }}
          range={range}
          strings={strings}
        />
      </div>
    );
  }

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name={`${ID}-mode`}
        onChange={setMode}
        options={[
          { value: "series", label: strings.modeSeries },
          { value: "cfu", label: strings.modeCfu },
        ]}
        value={mode}
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <TextField
          hint={strings.factorHint}
          id={`${ID}-factor`}
          label={strings.factor}
          numeric
          onChange={setFactor}
          value={factor}
        />
        <TextField
          hint={t(strings.stepsHint, { max: String(MAX_STEPS) })}
          id={`${ID}-steps`}
          label={strings.steps}
          numeric
          onChange={setSteps}
          value={steps}
        />
      </div>
      {mode === "series" ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <TextField
            id={`${ID}-start`}
            label={strings.startConc}
            numeric
            onChange={setStartConc}
            value={startConc}
          />
          <TextField
            id={`${ID}-concunit`}
            label={strings.concUnit}
            onChange={setConcUnit}
            placeholder={strings.concUnitPlaceholder}
            value={concUnit}
          />
          <TextField
            id={`${ID}-volume`}
            label={strings.volume}
            numeric
            onChange={setVolume}
            value={volume}
          />
          <SelectField
            id={`${ID}-vunit`}
            label={strings.volumeUnit}
            onChange={setVolumeUnit}
            options={unitOptions}
            value={volumeUnit}
          />
        </div>
      ) : null}
      {mode === "cfu" ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <TextField
            hint={strings.countsHint}
            id={`${ID}-counts`}
            label={strings.countsLabel}
            onChange={setCounts}
            value={counts}
          />
          <SelectField
            id={`${ID}-tube`}
            label={strings.platedTube}
            onChange={setPlatedTube}
            options={tubeOptions}
            value={platedTube}
          />
          <TextField
            id={`${ID}-plated`}
            label={strings.platedVolume}
            numeric
            onChange={setPlatedVolume}
            value={platedVolume}
          />
          <SelectField
            id={`${ID}-punit`}
            label={strings.platedUnit}
            onChange={setPlatedUnit}
            options={unitOptions}
            value={platedUnit}
          />
          <TextField
            hint={strings.preDilutionHint}
            id={`${ID}-pre`}
            label={strings.preDilution}
            numeric
            onChange={setPreDilution}
            value={preDilution}
          />
          <SelectField
            id={`${ID}-range`}
            label={strings.rangeLabel}
            onChange={setRange}
            options={[
              { value: "textbook", label: strings.rangeTextbook },
              { value: "apha", label: strings.rangeApha },
              { value: "iso", label: strings.rangeIso },
            ]}
            value={range}
          />
        </div>
      ) : null}
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {renderResults()}
      </section>
    </>
  );
}
